class ProductVariantSwitcher extends HTMLElement {
  constructor() {
    super();

    this.productData = null;
    this.currentVariantId = null;
    this.mediaItems = [];
    this.swatches = [];
    this.priceElements = {};
    this.formElements = {};

    this.handleSwatchClick = this.handleSwatchClick.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.swatches.forEach(swatch => {
      swatch.removeEventListener('click', this.handleSwatchClick);
    });
  }

  init() {
    const dataScript = this.querySelector('#product-variant-data');
    if (!dataScript) {
      return;
    }

    try {
      this.productData = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    this.mediaItems = Array.from(this.querySelectorAll('[data-variant-media]'));

    this.swatches = Array.from(this.querySelectorAll('[data-variant-swatch]'));

    this.priceElements = {
      price: this.querySelector('[data-product-price]'),
      comparePrice: this.querySelector('[data-product-compare-price]'),
      badge: this.querySelector('[data-product-sale-badge]')
    };

    const productForm = this.querySelector('product-form');
    if (productForm) {
      const form = productForm.querySelector('form');
      if (form) {
        this.formElements = {
          variantInput: form.querySelector('input[name="id"]'),
          submitButton: form.querySelector('button[type="submit"]')
        };
      }
    }

    if (this.formElements.variantInput) {
      this.currentVariantId = parseInt(this.formElements.variantInput.value);
    }

    if (this.currentVariantId) {
      const initialVariant = this.productData.variants.find(v => v.id === this.currentVariantId);
      if (initialVariant) {
        this.updatePrice(initialVariant);
        this.updateForm(initialVariant);
      }
    }

    this.updateMediaVisibility(this.currentVariantId);
    this.updateSwatchesState(this.currentVariantId);

    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', this.handleSwatchClick);
    });
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;
    const isAvailable = swatch.dataset.variantAvailable === 'true';

    const seedVariantId = parseInt(swatch.dataset.variantId);
    const optionPositionRaw = swatch.dataset.optionPosition;
    const optionValue = swatch.dataset.optionValue;

    let variant = null;
    if (optionPositionRaw && optionValue !== undefined) {
      const position = parseInt(optionPositionRaw, 10);
      const currentVariant = this.productData.variants.find(v => v.id === this.currentVariantId);
      const desiredOptions = [
        currentVariant?.option1 ?? null,
        currentVariant?.option2 ?? null,
        currentVariant?.option3 ?? null,
      ];
      desiredOptions[position - 1] = optionValue;
      variant = this.productData.variants.find(v =>
        v.option1 === desiredOptions[0] &&
        v.option2 === desiredOptions[1] &&
        v.option3 === desiredOptions[2]
      );
    }
    variant = variant ?? this.productData.variants.find(v => v.id === seedVariantId);

    if (!variant) {
      return;
    }

    const variantId = variant.id;
    this.currentVariantId = variantId;

    this.updateSwatchesState(variantId);
    this.updateMediaVisibility(variantId);
    this.updatePrice(variant);
    this.updateForm(variant);
    this.updateVariantTitle(variant);
    this.updateURL(variantId);

    // Dispatch variant changed event
    const variantChangedEvent = new CustomEvent('variant:changed', {
      detail: {
        variantId,
        variant
      },
      bubbles: true
    });
    document.dispatchEvent(variantChangedEvent);

    const swatchStrip = swatch.closest('variant-selector');
    if (!swatchStrip) {
      return;
    }
    const stripRect = swatchStrip.getBoundingClientRect();
    const swatchRect = swatch.getBoundingClientRect();
    const targetLeft = swatchStrip.scrollLeft + (swatchRect.left - stripRect.left) - (stripRect.width - swatchRect.width) / 2;
    swatchStrip.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }

  updateSwatchesState(selectedVariantId) {
    const selectedVariant = this.productData.variants.find(v => v.id === selectedVariantId);

    this.swatches.forEach(swatch => {
      const isSizeSwatch = swatch.classList.contains('size-swatch');
      const optionPositionRaw = swatch.dataset.optionPosition;
      const optionValue = swatch.dataset.optionValue;

      let isSelected = false;
      if (optionPositionRaw && optionValue !== undefined && selectedVariant) {
        const position = parseInt(optionPositionRaw, 10);
        isSelected = selectedVariant[`option${position}`] === optionValue;
      } else {
        const seedVariantId = parseInt(swatch.dataset.variantId);
        isSelected = seedVariantId === selectedVariantId;
      }

      if (isSelected) {
        if (!isSizeSwatch) {
          swatch.classList.remove('border-transparent', 'hover:border-black');
          swatch.classList.add('border-black');
        }
        swatch.classList.add('active');
        swatch.setAttribute('aria-pressed', 'true');
        return;
      }
      if (!isSizeSwatch) {
        swatch.classList.remove('border-black');
        swatch.classList.add('border-transparent', 'hover:border-black');
      }
      swatch.classList.remove('active');
      swatch.setAttribute('aria-pressed', 'false');
    });
  }

  updateMediaVisibility(variantId) {
    const variant = this.productData.variants.find(v => v.id === variantId);
    if (!variant || !variant.media_range) {
      return;
    }

    const { start, end } = variant.media_range;

    if (start > end) {
      return;
    }

    this.mediaItems.forEach(mediaItem => {
      const mediaIndex = parseInt(mediaItem.dataset.mediaIndex);

      if (mediaIndex >= start && mediaIndex <= end) {
        mediaItem.classList.remove('hidden');
        mediaItem.classList.add('block');
      } else {
        mediaItem.classList.remove('block');
        mediaItem.classList.add('hidden');
      }
    });

    this.updateMediaGridLayout();
    this.updateBadgeVisibility(start);

    const mobileGalleryScroller = document.querySelector('.product-media-mobile [data-product-gallery-scroll]');
    if (mobileGalleryScroller) {
      const firstVisibleMedia = this.mediaItems.find(item => {
        const mediaIndex = parseInt(item.dataset.mediaIndex);
        return mediaIndex === start && !item.classList.contains('hidden');
      });

      if (firstVisibleMedia) {
        const scrollerRect = mobileGalleryScroller.getBoundingClientRect();
        const mediaRect = firstVisibleMedia.getBoundingClientRect();
        const targetLeft = mobileGalleryScroller.scrollLeft + (mediaRect.left - scrollerRect.left);
        mobileGalleryScroller.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }
    }

    if (window.productGalleryDots) {
      window.productGalleryDots.refresh();
    }
  }

  updateBadgeVisibility(firstVisibleMediaIndex) {
    const mobileBadges = Array.from(document.querySelectorAll('[data-product-badge-mobile]'));
    const desktopBadges = Array.from(document.querySelectorAll('[data-product-badge-desktop]'));

    mobileBadges.forEach(badge => {
      const badgeIndex = parseInt(badge.dataset.badgeIndex);
      if (badgeIndex === firstVisibleMediaIndex) {
        badge.classList.remove('opacity-0', 'hidden');
      } else {
        badge.classList.add('opacity-0', 'hidden');
      }
    });

    desktopBadges.forEach(badge => {
      const badgeIndex = parseInt(badge.dataset.badgeIndex);
      if (badgeIndex === firstVisibleMediaIndex) {
        badge.classList.remove('opacity-0', 'hidden');
      } else {
        badge.classList.add('opacity-0', 'hidden');
      }
    });
  }

  updateMediaGridLayout() {
    const desktopItems = Array.from(document.querySelectorAll('.product-media-desktop [data-variant-media]'));
    const visibleMedia = desktopItems.filter(item => !item.classList.contains('hidden'));

    visibleMedia.forEach((item, index) => {
      const cyclePosition = (index + 1) % 3;

      item.classList.remove('col-span-1', 'col-span-2');

      if (cyclePosition === 1) {
        item.classList.add('col-span-2');
      } else {
        item.classList.add('col-span-1');
      }
    });
  }

  updatePrice(variant) {
    if (!this.priceElements.price) return;

    const hasDiscount = variant.compare_at_price > variant.price;

    this.priceElements.price.textContent = hasDiscount
      ? variant.price_formatted
      : `MRP: ${variant.price_formatted}`;

    if (this.priceElements.comparePrice) {
      if (hasDiscount) {
        this.priceElements.comparePrice.textContent = `MRP: ${variant.compare_at_price_formatted}`;
        this.priceElements.comparePrice.classList.remove('hidden');
      } else {
        this.priceElements.comparePrice.classList.add('hidden');
      }
    }

    if (this.priceElements.badge) {
      if (hasDiscount) {
        this.priceElements.badge.classList.remove('hidden');
      } else {
        this.priceElements.badge.classList.add('hidden');
      }
    }

    this.updateEmiVisibility(variant);
  }

  updateEmiVisibility(variant) {
    const emiContainer = document.getElementById('buy-with-emi-btn');
    if (!emiContainer) return;

    const minAmount = Number(emiContainer.dataset.emiMinAmount ?? 0);
    const isEligible = variant.price >= minAmount;

    emiContainer.classList.toggle('hidden', !isEligible);
  }

  updateForm(variant) {
    if (!this.formElements.variantInput) return;

    this.formElements.variantInput.value = variant.id;

    const productForms = document.querySelectorAll('product-form');
    productForms.forEach(productForm => {
      if (productForm.closest('product-recommendations')) return; 
      productForm.dataset.variantId = variant.id;
      productForm.dataset.variantTitle = variant.title;
      productForm.dataset.variantPrice = variant.price;

      if (variant.featured_media?.image_url) {
        productForm.dataset.productImage = variant.featured_media.image_url;
      }

      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      const submitButton = productForm.querySelector('button[type="submit"]');
      if (submitButton) {
        const buttonTextEl = submitButton.querySelector('[data-button-text]');

        if (variant.available) {
          submitButton.disabled = false;
          submitButton.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
          submitButton.classList.add('hover:bg-gray-800');
          submitButton.setAttribute('aria-disabled', 'false');
          const isOosContinueSellingOn =
            (variant.inventory_quantity || 0) <= 0 && variant.inventory_policy === 'continue';
          const nextText = isOosContinueSellingOn ? 'Pre order' : 'Add to Cart';
          if (buttonTextEl) {
            buttonTextEl.textContent = nextText;
          } else {
            // Fallback for forms that don't use the loader/text spans
            submitButton.textContent = nextText;
          }
        } else {
          submitButton.disabled = true;
          submitButton.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
          submitButton.classList.remove('hover:bg-gray-800');
          submitButton.setAttribute('aria-disabled', 'true');
          if (buttonTextEl) {
            buttonTextEl.textContent = 'Sold out';
          } else {
            // Fallback for forms that don't use the loader/text spans
            submitButton.textContent = 'Sold out';
          }
        }
      }
    });
  }

  updateVariantTitle(variant) {
    const titleElement = this.querySelector(':not(product-form)[data-variant-title]');
    if (titleElement) {
      titleElement.textContent = variant.title;
    }
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }
}

customElements.define('product-variant-switcher', ProductVariantSwitcher);

class ProductGalleryDots {
  constructor() {
    this.scrollContainer = null;
    this.leftDot = null;
    this.rightDot = null;
    this.dotsContainer = null;
    this.mediaItems = [];

    this.handleScroll = this.handleScroll.bind(this);
  }

  init() {
    this.scrollContainer = document.querySelector('[data-product-gallery-scroll]');
    this.leftDot = document.querySelector('[data-dot-left]');
    this.rightDot = document.querySelector('[data-dot-right]');
    this.dotsContainer = document.querySelector('[data-gallery-dots]');

    if (!this.scrollContainer || !this.leftDot || !this.rightDot) return;

    this.attachScrollListener();

    this.syncDotsWithMedia();
  }

  syncDotsWithMedia() {
    this.mediaItems = Array.from(
      document.querySelectorAll('.product-media-mobile [data-variant-media]')
    ).filter(item => !item.classList.contains('hidden'));

    const visibleMediaCount = this.mediaItems.length;

    if (this.dotsContainer) {
      if (visibleMediaCount > 1) {
        this.dotsContainer.classList.remove('hidden');
      } else {
        this.dotsContainer.classList.add('hidden');
      }
    }

    this.scrollContainer.scrollLeft = 0;

    this.updateIndicator();
  }

  attachScrollListener() {
    let ticking = false;

    this.scrollContainer.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  handleScroll() {
    this.updateIndicator();
  }

  updateIndicator() {
    const scrollLeft = this.scrollContainer.scrollLeft;
    const scrollWidth = this.scrollContainer.scrollWidth;
    const containerWidth = this.scrollContainer.offsetWidth;
    const threshold = 5;

    const isAtStart = scrollLeft <= threshold;
    const isAtEnd = scrollLeft + containerWidth >= scrollWidth - threshold;

    if (isAtStart) {
      this.leftDot.classList.remove('bg-[#FFC314]');
      this.leftDot.classList.add('bg-[#FFFFFF38]');
    } else {
      this.leftDot.classList.remove('bg-[#FFFFFF38]');
      this.leftDot.classList.add('bg-[#FFC314]');
    }

    if (isAtEnd) {
      this.rightDot.classList.remove('bg-[#FFC314]');
      this.rightDot.classList.add('bg-[#FFFFFF38]');
    } else {
      this.rightDot.classList.remove('bg-[#FFFFFF38]');
      this.rightDot.classList.add('bg-[#FFC314]');
    }
  }

  refresh() {
    this.syncDotsWithMedia();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const productSection = document.getElementById('product-wrapper');
  if (!productSection) return;

  function initVideoControls() {
    const videoWrappers = productSection.querySelectorAll('[data-video-wrapper]');

    videoWrappers.forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      const playPauseBtn = wrapper.querySelector('[data-video-play-pause]');

      if (!video || !playPauseBtn) return;

      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      const lazySource = video.querySelector('source[data-src]');
      if (lazySource) {
        const originalSrc = lazySource.getAttribute('data-src');
        lazySource.removeAttribute('data-src');
        lazySource.setAttribute('src', originalSrc);
        video.load();
      }

      playPauseBtn.addEventListener('click', () => {
        if (video.paused) {
          video.play().then(() => {
            wrapper.classList.add('is-playing');
          }).catch((e) => {
            console.error('Error playing video:', e);
          });
        } else {
          video.pause();
          wrapper.classList.remove('is-playing');
        }
      });

      video.addEventListener('play', () => {
        wrapper.classList.add('is-playing');
      });

      video.addEventListener('pause', () => {
        wrapper.classList.remove('is-playing');
      });

      video.addEventListener('click', () => {
        playPauseBtn.click();
      });
    });
  }

  function initSizeSetThumbs() {
    for (const thumb of document.querySelectorAll('.size-set-thumb')) {
      const scroller = document.getElementById(thumb.dataset.scroller);
      const track = document.querySelector(`[data-scroll-track="${thumb.dataset.scroller}"]`);
      if (!scroller || !track) continue;

      const update = () => {
        const { scrollLeft, scrollWidth, clientWidth } = scroller;
        const hasOverflow = scrollWidth > clientWidth;

        track.classList.toggle('hidden', !hasOverflow);

        if (!hasOverflow) return;

        const ratio = clientWidth / scrollWidth;
        const thumbWidth = ratio * 100;
        const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbWidth);
        thumb.style.width = `${thumbWidth}%`;
        thumb.style.transform = `translateX(${(thumbLeft / thumbWidth) * 100}%)`;
      };

      update();
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }
  }

  initVideoControls();
  initSizeSetThumbs();

  const galleryDots = new ProductGalleryDots();
  galleryDots.init();

  window.productGalleryDots = galleryDots;

  const buyWithEmiInfoBtn = document.getElementById('buy-with-emi-info-btn');
  if (buyWithEmiInfoBtn) {
    buyWithEmiInfoBtn.addEventListener('click', () => {
      const smWidgetBtn = document.getElementById('sm-widget-btn');
      if (smWidgetBtn) {
        smWidgetBtn.click();
      } else {
        console.warn('sm-widget-btn element not found');
      }
    });
  }

  function updateDownPayment() {
    const dpElement = document.getElementById('dp');
    const downPaymentDisplay = document.querySelector('.down-payment');

    if (dpElement && downPaymentDisplay) {
      const dpValue = dpElement.textContent.trim();
      if (dpValue) {
        downPaymentDisplay.textContent = `₹${dpValue}`;
        return true;
      }
    }
    return false;
  }

  function setupDownPaymentWatcher() {
    function setupObserver() {
      const dpElement = document.getElementById('dp');
      if (dpElement) {
        const observer = new MutationObserver(() => {
          updateDownPayment();
        });
        observer.observe(dpElement, {
          childList: true,
          characterData: true,
          subtree: true
        });
        return true;
      }
      return false;
    }

    if (setupObserver()) {
      updateDownPayment();
      return;
    }

    let pollCount = 0;
    const maxPolls = 40;

    const pollInterval = setInterval(() => {
      pollCount++;

      if (setupObserver()) {
        clearInterval(pollInterval);
        updateDownPayment();
        return;
      }

      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
      }
    }, 500);
  }

  const variantSwitcher = document.querySelector('product-variant-switcher');
  if (variantSwitcher) {
    const variantInput = variantSwitcher.querySelector('input[name="id"]');
    if (variantInput) {
      let lastVariantId = variantInput.value;

      const variantObserver = new MutationObserver(() => {
        const currentVariantId = variantInput.value;
        if (currentVariantId !== lastVariantId) {
          lastVariantId = currentVariantId;
          setTimeout(() => {
            let checkCount = 0;
            const maxChecks = 20;

            const checkInterval = setInterval(() => {
              checkCount++;
              if (updateDownPayment() || checkCount >= maxChecks) {
                clearInterval(checkInterval);
              }
            }, 500);
          }, 500);
        }
      });

      variantObserver.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      variantInput.addEventListener('change', () => {
        setTimeout(() => {
          let checkCount = 0;
          const maxChecks = 20;

          const checkInterval = setInterval(() => {
            checkCount++;
            if (updateDownPayment() || checkCount >= maxChecks) {
              clearInterval(checkInterval);
            }
          }, 500);
        }, 500);
      });
    }
  }

  // ============================================
  // Customize Button & Modal Integration
  // ============================================

  const customizeBtns = productSection?.querySelectorAll('[data-customize-button]');
  const productForms = productSection?.querySelectorAll('product-form');
  const productId = customizeBtns?.[0]?.dataset.productId;

  const restoreSavedCustomization = (variantId) => {
    if (!productId || !variantId || !productForms || productForms.length === 0) return;

    const storageKey = `customization_${productId}_${variantId}`;
    const savedCustomization = localStorage.getItem(storageKey);

    if (savedCustomization) {
      try {
        productForms.forEach(productForm => {
          productForm.dataset.customizationData = savedCustomization;
        });
      } catch (e) {
        console.error('[Product] Failed to parse saved customization:', e);
        localStorage.removeItem(storageKey);
      }
    } else {
      productForms.forEach(productForm => {
        delete productForm.dataset.customizationData;
      });
    }
  };

  if (variantSwitcher && productId) {
    restoreSavedCustomization(variantSwitcher.currentVariantId);
  }

  if (productId && variantSwitcher) {
    document.addEventListener('customization:applied', (event) => {
      const customizationData = event.detail;
      if (!customizationData) return;

      const currentVariantId = variantSwitcher.currentVariantId;
      if (!currentVariantId) return;

      const storageKey = `customization_${productId}_${currentVariantId}`;

      const hasContent = !!(
        (customizationData.text && customizationData.text.content) ||
        customizationData.pattern ||
        customizationData.color
      );

      if (hasContent) {
        localStorage.setItem(storageKey, JSON.stringify(customizationData));

        if (productForms && productForms.length > 0) {
          productForms.forEach(productForm => {
            productForm.dataset.customizationData = JSON.stringify(customizationData);
          });
        }
      } else {
        localStorage.removeItem(storageKey);

        if (productForms && productForms.length > 0) {
          productForms.forEach(productForm => {
            delete productForm.dataset.customizationData;
          });
        }
      }
    });
  }

  if (customizeBtns && customizeBtns.length > 0 && variantSwitcher) {
    customizeBtns.forEach(customizeBtn => {
      customizeBtn.addEventListener('click', (e) => {
        e.preventDefault();

        const productId = customizeBtn.dataset.productId;
        const modal = document.getElementById(`product-customize-modal-${productId}`);

        if (!modal) {
          console.error('[Customize] Modal not found:', `product-customize-modal-${productId}`);
          return;
        }

        const productTabs = productSection?.querySelector('product-tabs');

        if (productTabs?.getActiveTab?.() === 'design') {
          const selectedDesignVariantId = productTabs.getSelectedDesignVariantId?.();
          if (selectedDesignVariantId && productTabs.openCustomizeModalForVariant) {
            productTabs.openCustomizeModalForVariant(selectedDesignVariantId);
            return;
          }
        }

        const currentVariantId = variantSwitcher.currentVariantId;
        const variant = variantSwitcher.productData?.variants?.find(v => v.id === currentVariantId);

        if (!variant) {
          console.error('[Customize] Variant not found:', currentVariantId);
          return;
        }

        const isMokoPin = modal.dataset.hasMokoPin === 'true';

        if (!isMokoPin && !variant.engraving_image_url) {
          console.warn('[Customize] No engraving image available for variant:', currentVariantId);
          return;
        }

        const storageKey = `customization_${productId}_${currentVariantId}`;
        const savedDataRaw = localStorage.getItem(storageKey);
        let savedData = null;
        if (savedDataRaw) {
          try {
            savedData = JSON.parse(savedDataRaw);
          } catch (err) {
            console.error('[Customize] Failed to parse saved customization:', err);
          }
        }

        if (!savedData && modal.resetToNewDesign) {
          modal.resetToNewDesign();
        }

        if (isMokoPin) {
          const tabSelectedPin = productTabs?.querySelector('[data-moko-pin-container] [data-selected="true"]');
          if (tabSelectedPin?.dataset.mokoPinVariantId && modal.setSelectedMokoPinVariant) {
            modal.setSelectedMokoPinVariant(tabSelectedPin.dataset.mokoPinVariantId);
          }
        } else if (modal.updateEngravingData) {
          modal.updateEngravingData(
            variant.engraving_image_url,
            variant.engraving_position,
            variant.engraving_font_size,
            variant.price,
            variant.price_formatted,
            variant.compare_at_price,
            variant.compare_at_price_formatted
          );
        }

        if (savedData && productTabs?.loadSavedCustomization) {
          productTabs.loadSavedCustomization(modal, savedData);
        }

        modal.open();
      });
    });

    const originalUpdateVariant = variantSwitcher.updateVariant?.bind(variantSwitcher);
    if (originalUpdateVariant) {
      variantSwitcher.updateVariant = function (variantId) {
        originalUpdateVariant(variantId);

        const variant = this.productData?.variants?.find(v => v.id === variantId);

        customizeBtns.forEach(customizeBtn => {
          if (variant?.engraving_image_url) {
            customizeBtn.classList.remove('hidden', 'opacity-0', 'pointer-events-none');
            customizeBtn.classList.add('block', 'opacity-100');
          } else {
            customizeBtn.classList.add('hidden', 'opacity-0', 'pointer-events-none');
            customizeBtn.classList.remove('block', 'opacity-100');
          }
        });

        const productId = customizeBtns[0]?.dataset.productId;
        const modal = document.getElementById(`product-customize-modal-${productId}`);

        if (modal && !modal.classList.contains('invisible') && modal.updateEngravingData && variant?.engraving_image_url) {
          modal.updateEngravingData(
            variant.engraving_image_url,
            variant.engraving_position,
            variant.engraving_font_size,
            variant.price,
            variant.price_formatted,
            variant.compare_at_price,
            variant.compare_at_price_formatted
          );
        }

        if (variantId && restoreSavedCustomization) {
          restoreSavedCustomization(variantId);
        }
      };
    }
  }

  setupDownPaymentWatcher();

  const mobileStickyForm = productSection.querySelector('[data-mobile-sticky-form]');
  if (!mobileStickyForm) {
    return;
  }

  document.body.classList.add('pb-24', 'lg:pb-0');

  const footer = document.querySelector('footer');
  if (footer) {
    const footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            mobileStickyForm.classList.add('is-hidden');
          } else {
            mobileStickyForm.classList.remove('is-hidden');
          }
        });
      },
      { threshold: 0 }
    );
    footerObserver.observe(footer);
  }
});
